// Guided Scan Security section for the Settings page.
// Manages: passkey enrollment / rename / delete, recovery codes (one-shot
// display), trusted-device list / rename / revoke, and "Sign out everywhere".

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, KeyRound, Copy, Trash2, Pencil, LogOut, AlertTriangle, Fingerprint } from 'lucide-react';
import {
  registerPasskey,
  listPasskeys,
  renamePasskey,
  deletePasskey,
  listTrustedDevices,
  renameTrustedDevice,
  revokeTrustedDevice,
  revokeAllSessions,
  generateRecoveryCodes,
} from '@/lib/guidedScan';

export const SecuritySection = () => {
  const [passkeys, setPasskeys] = useState([]);
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newCodes, setNewCodes] = useState(null); // one-shot display
  const [passkeyLabel, setPasskeyLabel] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renamingLabel, setRenamingLabel] = useState('');
  const [renamingDeviceId, setRenamingDeviceId] = useState(null);
  const [renamingDeviceLabel, setRenamingDeviceLabel] = useState('');

  const load = async () => {
    try {
      const [p, d] = await Promise.all([listPasskeys(), listTrustedDevices()]);
      setPasskeys(p.items || []);
      setDevices(d.items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const enroll = async () => {
    if (!window.PublicKeyCredential) {
      toast.error('This browser does not support passkeys. Try Chrome, Edge, Safari, or Firefox on a modern device.');
      return;
    }
    setBusy(true);
    try {
      await registerPasskey({ label: passkeyLabel || 'Passkey' });
      toast.success('Passkey added. Guided Scans are ready to go.');
      setPasskeyLabel('');
      await load();
    } catch (e) {
      const msg = e?.message || e?.response?.data?.detail || 'Passkey registration failed.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const doRenamePasskey = async () => {
    if (!renamingId || !renamingLabel.trim()) return;
    try {
      await renamePasskey(renamingId, renamingLabel.trim());
      toast.success('Passkey renamed.');
      setRenamingId(null);
      setRenamingLabel('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rename failed.'); }
  };

  const doDeletePasskey = async (id, label) => {
    if (!window.confirm(`Delete passkey "${label}"? Guided Scan authorizations granted by this passkey will be revoked.`)) return;
    try {
      await deletePasskey(id);
      toast.success('Passkey removed.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed.'); }
  };

  const doRenameDevice = async () => {
    if (!renamingDeviceId || !renamingDeviceLabel.trim()) return;
    try {
      await renameTrustedDevice(renamingDeviceId, renamingDeviceLabel.trim());
      toast.success('Device renamed.');
      setRenamingDeviceId(null);
      setRenamingDeviceLabel('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rename failed.'); }
  };

  const doRevokeDevice = async (id, label) => {
    if (!window.confirm(`Revoke device "${label}"? All Guided Scan authorizations for it will be revoked.`)) return;
    try {
      await revokeTrustedDevice(id);
      toast.success('Device revoked.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Revoke failed.'); }
  };

  const doRevokeAll = async () => {
    if (!window.confirm('Sign out every device and revoke every Guided Scan authorization? You will need to sign in again.')) return;
    try {
      await revokeAllSessions();
      toast.success('All sessions revoked. Sign in again to continue.');
      window.setTimeout(() => { window.location.href = '/login'; }, 1200);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to revoke.'); }
  };

  const genCodes = async () => {
    if (!window.confirm('Generate a fresh set of recovery codes? Any existing unused codes will be invalidated.')) return;
    setBusy(true);
    try {
      const res = await generateRecoveryCodes();
      setNewCodes(res.codes);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not generate codes.'); }
    finally { setBusy(false); }
  };

  return (
    <section className="zita-card p-6 space-y-6" data-testid="settings-security">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} className="ari-accent" />
        <h2 className="font-serif text-2xl text-zita-espresso">Guided Scan security</h2>
      </div>
      <p className="text-sm text-zita-mocha -mt-2">
        Guided Scans require a device-bound step-up before Zita will resume any source that
        needs your presence (CAPTCHA, identity confirmation, MFA). Enroll a passkey on every
        device you scan from, and keep a set of recovery codes somewhere safe.
      </p>

      {/* PASSKEYS */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Fingerprint size={16} className="text-zita-cocoa" />
          <h3 className="font-serif text-lg text-zita-espresso">Passkeys</h3>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            className="zita-input flex-1 px-3 py-2 text-sm"
            placeholder="Passkey label (e.g. MacBook Touch ID)"
            value={passkeyLabel}
            onChange={(e) => setPasskeyLabel(e.target.value)}
            data-testid="security-passkey-label-input"
          />
          <button
            type="button"
            onClick={enroll}
            disabled={busy}
            className="zita-btn-primary text-sm"
            data-testid="security-passkey-enroll"
          >
            Add passkey
          </button>
        </div>
        <ul className="space-y-2" data-testid="security-passkey-list">
          {passkeys.length === 0 && (
            <li className="text-xs text-zita-muted italic">No passkeys enrolled yet. Guided Scans will require a recovery code until you add one.</li>
          )}
          {passkeys.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 border border-zita-taupe rounded-lg px-3 py-2 text-sm" data-testid={`security-passkey-row-${p.id}`}>
              {renamingId === p.id ? (
                <div className="flex-1 flex gap-2 min-w-0">
                  <input value={renamingLabel} onChange={(e) => setRenamingLabel(e.target.value)} className="zita-input flex-1 px-2 py-1 text-sm" />
                  <button type="button" onClick={doRenamePasskey} className="zita-btn-primary text-xs">Save</button>
                  <button type="button" onClick={() => { setRenamingId(null); setRenamingLabel(''); }} className="zita-btn-secondary text-xs">Cancel</button>
                </div>
              ) : (
                <>
                  <div className="min-w-0">
                    <div className="text-zita-espresso font-medium truncate">{p.label}</div>
                    <div className="text-[11px] text-zita-muted">
                      Added {new Date(p.created_at).toLocaleDateString()} · last used {p.last_used_at ? new Date(p.last_used_at).toLocaleDateString() : 'never'}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => { setRenamingId(p.id); setRenamingLabel(p.label); }} className="p-1.5 rounded-full hover:bg-zita-sand text-zita-cocoa" aria-label="Rename" data-testid={`security-passkey-rename-${p.id}`}>
                      <Pencil size={13} />
                    </button>
                    <button type="button" onClick={() => doDeletePasskey(p.id, p.label)} className="p-1.5 rounded-full hover:bg-zita-sand text-red-600" aria-label="Delete" data-testid={`security-passkey-delete-${p.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* RECOVERY CODES */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <KeyRound size={16} className="text-zita-cocoa" />
          <h3 className="font-serif text-lg text-zita-espresso">Recovery codes</h3>
        </div>
        <p className="text-xs text-zita-mocha">
          10 one-time codes you can use if you lose your passkey. Store them in a password
          manager — Zita only stores hashes and cannot show them again.
        </p>
        <button
          type="button"
          onClick={genCodes}
          disabled={busy}
          className="zita-btn-secondary text-sm"
          data-testid="security-recovery-generate"
        >
          Generate 10 fresh recovery codes
        </button>
        {newCodes && (
          <div className="border border-amber-400 bg-amber-50 rounded-lg p-3 space-y-2" data-testid="security-recovery-panel">
            <div className="flex items-center gap-2 text-amber-700 text-sm font-semibold">
              <AlertTriangle size={14} /> Save these NOW — Zita will not show them again
            </div>
            <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
              {newCodes.map((c) => (
                <li key={c} className="bg-white border border-amber-300 rounded px-2 py-1 text-center tracking-widest" data-testid={`security-recovery-code-${c}`}>{c}</li>
              ))}
            </ul>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(newCodes.join('\n')); toast.success('Codes copied.'); }}
                className="zita-btn-secondary text-xs inline-flex items-center gap-1"
                data-testid="security-recovery-copy"
              >
                <Copy size={12} /> Copy all
              </button>
              <button
                type="button"
                onClick={() => setNewCodes(null)}
                className="zita-btn-primary text-xs"
                data-testid="security-recovery-done"
              >
                I&apos;ve saved them
              </button>
            </div>
          </div>
        )}
      </div>

      {/* TRUSTED DEVICES */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-zita-cocoa" />
          <h3 className="font-serif text-lg text-zita-espresso">Trusted devices</h3>
        </div>
        <ul className="space-y-2" data-testid="security-device-list">
          {devices.length === 0 && (
            <li className="text-xs text-zita-muted italic">No devices have completed Guided Scan step-up yet.</li>
          )}
          {devices.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 border border-zita-taupe rounded-lg px-3 py-2 text-sm" data-testid={`security-device-row-${d.id}`}>
              {renamingDeviceId === d.id ? (
                <div className="flex-1 flex gap-2 min-w-0">
                  <input value={renamingDeviceLabel} onChange={(e) => setRenamingDeviceLabel(e.target.value)} className="zita-input flex-1 px-2 py-1 text-sm" />
                  <button type="button" onClick={doRenameDevice} className="zita-btn-primary text-xs">Save</button>
                  <button type="button" onClick={() => { setRenamingDeviceId(null); setRenamingDeviceLabel(''); }} className="zita-btn-secondary text-xs">Cancel</button>
                </div>
              ) : (
                <>
                  <div className="min-w-0">
                    <div className="text-zita-espresso font-medium truncate flex items-center gap-2">
                      {d.label}
                      {d.revoked && <span className="op-low text-[10px] px-2 py-0.5 rounded-full">revoked</span>}
                    </div>
                    <div className="text-[11px] text-zita-muted">
                      Last seen {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : '—'} · {d.passkey_count} passkey{d.passkey_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {!d.revoked && (
                      <>
                        <button type="button" onClick={() => { setRenamingDeviceId(d.id); setRenamingDeviceLabel(d.label); }} className="p-1.5 rounded-full hover:bg-zita-sand text-zita-cocoa" aria-label="Rename" data-testid={`security-device-rename-${d.id}`}>
                          <Pencil size={13} />
                        </button>
                        <button type="button" onClick={() => doRevokeDevice(d.id, d.label)} className="p-1.5 rounded-full hover:bg-zita-sand text-red-600" aria-label="Revoke" data-testid={`security-device-revoke-${d.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* REVOKE ALL */}
      <div className="pt-3 border-t border-zita-taupe/60">
        <button
          type="button"
          onClick={doRevokeAll}
          className="text-sm inline-flex items-center gap-2 text-red-600 hover:underline"
          data-testid="security-revoke-all"
        >
          <LogOut size={13} /> Sign out of every device
        </button>
      </div>
    </section>
  );
};
